import { useState } from "react";
import { useParams } from "react-router";
import { useOutletContext, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Link,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Loader from "../../components/Loader";
import useGetExternalDocs from "../../hooks/useGetExternalDocs";
import useDeleteExternalDocMutation from "../../hooks/useDeleteExternalDocMutation";
import { downloadSignedFile } from "../../utils/helpers";

const isImageUrl = (url) => {
  if (!url) return false;
  const path = url.split("?")[0];
  return /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(path);
};

const headerCellSx = {
  backgroundColor: (theme) => theme.palette.grey[100],
  fontWeight: "bold",
  textTransform: "uppercase",
  fontSize: "0.85rem",
  color: "#424242",
  whiteSpace: "nowrap",
};

const bodyCellSx = {
  whiteSpace: "nowrap",
};

const ExternalDocumentsCase = () => {
  const { _id_, caseId } = useParams();
  const location = useLocation();

  const { trialFilter, setActive, setBreadcrumbTitle, isTrialLocked } = useOutletContext();
  setActive("documents");
  setBreadcrumbTitle("External Documents");

  const visitName =
    location.state?.visitName ||
    new URLSearchParams(location.search).get("visitName") ||
    (caseId === "no-visit" ? "No Visit" : "Visit");

  const isNoVisit = caseId === "no-visit";
  const visitId = isNoVisit ? "no_visit" : caseId;

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [documentToDeleteId, setDocumentToDeleteId] = useState(null);
  const [page, setPage] = useState(1);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewDocument, setViewDocument] = useState({ url: "", fileName: "" });

  const handleViewClick = (url, fileName) => {
    setViewDocument({ url, fileName });
    setViewOpen(true);
  };

  const { data: documentList, isLoading: isDocsLoading } = useGetExternalDocs({
    patientId: _id_,
    trialId: trialFilter,
    visitId,
    page,
    enabled: !!trialFilter,
  });

  const deleteMutation = useDeleteExternalDocMutation();

  const handleDeleteClick = (id) => {
    setDocumentToDeleteId(id);
    setConfirmDeleteOpen(true);
  };

  const confirmDelete = () => {
    if (documentToDeleteId) {
      deleteMutation.mutate(documentToDeleteId, {
        onSettled: () => {
          setConfirmDeleteOpen(false);
          setDocumentToDeleteId(null);
        },
      });
    }
  };

  const handlePageChange = (_event, newPage) => {
    setPage(newPage);
  };

  const TruncateFileName = ({ fileName, maxLength }) => {
    if (fileName.length <= maxLength) {
      return <span>{fileName}</span>;
    }
    const extIndex = fileName.lastIndexOf(".");
    const truncatedFileName = `${fileName.slice(0, maxLength - 4)}...${fileName.slice(extIndex + 1)}`;
    return (
      <Tooltip title={fileName}>
        <span>{truncatedFileName}</span>
      </Tooltip>
    );
  };

  const maxLength = 20;

  const pageCount = documentList?.count ? Math.ceil(documentList.count / 10) : 0;

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Documents &mdash; {visitName}</Typography>
      </Stack>

      {/* Document List */}
      <Paper variant="outlined">
        {isDocsLoading ? (
          <Loader />
        ) : (
          <TableContainer>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>File Name</TableCell>
                  <TableCell sx={headerCellSx}>Updated By</TableCell>
                  <TableCell sx={headerCellSx}>Updated At</TableCell>
                  <TableCell sx={headerCellSx}>Created At</TableCell>
                  <TableCell sx={headerCellSx}>Comment</TableCell>
                  <TableCell sx={headerCellSx}>Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {documentList?.results?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No documents found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {documentList?.results?.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell sx={bodyCellSx}>
                      <TruncateFileName
                        fileName={item?.file_name ? item.file_name : ""}
                        maxLength={maxLength}
                      />
                    </TableCell>
                    <TableCell sx={bodyCellSx}>{item.updated_by}</TableCell>
                    <TableCell sx={bodyCellSx}>
                      {new Date(item.updated_at + "Z").toDateString()},{" "}
                      {new Date(item.updated_at + "Z").toLocaleTimeString()}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      {new Date(item.created_at + "Z").toDateString()},{" "}
                      {new Date(item.created_at + "Z").toLocaleTimeString()}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...bodyCellSx,
                        whiteSpace: "pre-wrap",
                        maxWidth: 200,
                      }}
                    >
                      {item.comment || "-"}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Stack direction="row" spacing={1}>
                        <Link
                          component="button"
                          underline="hover"
                          onClick={() =>
                            handleViewClick(item.document, item.file_name || `document-${item.id}`)
                          }
                        >
                          View
                        </Link>

                        <Typography component="span" color="text.secondary">
                          /
                        </Typography>

                        <Link
                          component="button"
                          underline="hover"
                          onClick={() =>
                            downloadSignedFile(
                              item.document,
                              item.file_name || `document-${item.id}`
                            )
                          }
                        >
                          Download
                        </Link>

                        <Typography component="span" color="text.secondary">
                          /
                        </Typography>

                        {!isTrialLocked && (
                          <Link
                            component="button"
                            underline="hover"
                            color="error"
                            onClick={() => handleDeleteClick(item?.id)}
                          >
                            Delete
                          </Link>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: "divider" }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing {documentList?.results?.length ?? 0} out of {documentList?.count ?? 0}
          </Typography>

          {pageCount > 1 && (
            <Pagination
              count={pageCount}
              page={page}
              onChange={handlePageChange}
              size="small"
              color="primary"
            />
          )}
        </Stack>
      </Paper>

      {/* Document Viewer Dialog */}
      <Dialog
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { height: "90vh" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1,
          }}
        >
          <Typography variant="subtitle1" noWrap sx={{ maxWidth: "85%" }}>
            {viewDocument.fileName}
          </Typography>
          <IconButton size="small" onClick={() => setViewOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column" }}>
          {isImageUrl(viewDocument.url) ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 2,
                bgcolor: "grey.50",
              }}
            >
              <img
                src={viewDocument.url}
                alt={viewDocument.fileName}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            </Box>
          ) : (
            <iframe
              src={viewDocument.url}
              title={viewDocument.fileName}
              style={{ flex: 1, width: "100%", border: "none" }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Are You Sure?</DialogTitle>
        <DialogContent>
          <Typography>You want to delete this file?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)} color="inherit">
            No
          </Button>
          <Button onClick={confirmDelete} variant="contained" color="error">
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ExternalDocumentsCase;
